/* src/ui/hud/hudBar.ts — the persistent top bar (TASKS.md 2.7, PLAN.md Part 4 §3).
 *
 * Wordmark, LOYALTY, the money ticker, and the pills on the right. The pills open windows that
 * are empty until their phase lands — deliberately visible-but-empty rather than hidden, so
 * the shape of the game is legible from the first screen (the same call viewToggles.ts makes
 * for unbuilt view modes).
 */

import { gameStore } from '../../core/store.ts';
import { Component } from '../component.ts';
import { registerAction } from '../dispatch.ts';
import { el, text } from '../dom.ts';
import { formatCount } from '../format.ts';
import type { WindowManager } from '../windows/windowChrome.ts';
import { MoneyTicker } from './moneyTicker.ts';

interface HudBarProps {
  readonly windows: WindowManager;
}

/** Pills that exist in the bar but whose contents belong to a later phase. */
const PLACEHOLDER_PILLS: ReadonlyArray<{ id: string; label: string; phase: string }> = [
  { id: 'leaderboards', label: 'Leaderboards', phase: 'Phase 7' },
  { id: 'community', label: 'Community', phase: 'Phase 7' },
  { id: 'account', label: 'Account', phase: 'Phase 8' },
];

export class HudBar extends Component<HudBarProps> {
  #loyalty = text('0');

  protected render(): HTMLElement {
    const money = new MoneyTicker();

    // Market and Travel are real destinations, not placeholders; Market leads as primary.
    const pills = [
      el(
        'button',
        { class: 'gf-hud-pill gf-hud-pill--primary', attrs: { type: 'button' },
          data: { action: 'hud:market' } },
        'Market',
      ),
      el(
        'button',
        { class: 'gf-hud-pill', attrs: { type: 'button' }, data: { action: 'hud:travel' } },
        'Travel',
      ),
      ...PLACEHOLDER_PILLS.map((p) =>
        el(
          'button',
          {
            class: 'gf-hud-pill',
            attrs: { type: 'button' },
            data: { action: `hud:${p.id}` },
          },
          p.label,
        ),
      ),
    ];

    const root = el(
      'header',
      { class: 'gf-hud-bar' },
      el('span', { class: 'gf-hud-brand' }, 'GROUNDFLOOR'),
      el(
        'div',
        { class: 'gf-hud-stat' },
        el('span', { class: 'gf-hud-label' }, 'LOYALTY'),
        el('span', { class: 'gf-hud-value gf-hud-value--sm' }, this.#loyalty),
      ),
      money.el,
      el('div', { class: 'gf-hud-pills' }, ...pills),
    );

    // The ticker owns a rAF loop, so it has to be mounted rather than just embedded.
    money.mount(root);
    this.onCleanup(() => {
      money.unmount();
    });

    for (const p of PLACEHOLDER_PILLS) {
      this.onCleanup(
        registerAction(`hud:${p.id}`, () => {
          const win = this.props.windows.open({ id: p.id, title: p.label, width: 300 });
          win.body.replaceChildren(
            el(
              'p',
              { class: 'gf-win-empty' },
              `${p.label} arrives in ${p.phase}.`,
            ),
          );
        }),
      );
    }

    this.bind(
      (s) => s?.loyalty ?? 0,
      (loyalty) => {
        this.#loyalty.textContent = formatCount(loyalty);
      },
    );
    // bind() only fires on change, so seed from whatever the store already holds.
    this.#loyalty.textContent = formatCount(gameStore.get()?.loyalty ?? 0);

    return root;
  }
}
